import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { ChipModule } from 'primeng/chip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ChatService } from '../../services/chat.service';
import { DatasetService } from '../../services/dataset.service';
import { AuthService } from '../../services/auth.service';
import { ChatMessage, ChatRequest, ChatResponse, ConversationStarter } from '../../models/chat.model';
import { Dataset } from '../../models/dataset.model';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    DropdownModule,
    ChipModule,
    ProgressSpinnerModule,
    TooltipModule
  ],
  template: `
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <header class="bg-white shadow-sm border-b">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between items-center py-4">
            <div class="flex items-center space-x-4">
              <h1 class="text-xl font-semibold text-gray-900">Ask IMC RAG Chat</h1>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-500">Dataset:</span>
                <p-dropdown
                  [options]="datasets"
                  [(ngModel)]="selectedDataset"
                  optionLabel="name"
                  placeholder="Select a dataset"
                  (onChange)="onDatasetChange()"
                  styleClass="w-64"
                ></p-dropdown>
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-600">
                Welcome, {{ currentUser?.firstName }} {{ currentUser?.lastName }}
              </span>
              <p-button
                label="Logout"
                severity="secondary"
                size="small"
                (onClick)="logout()"
              ></p-button>
            </div>
          </div>
        </div>
      </header>

      <!-- Main Chat Area -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <!-- Chat Messages -->
          <div class="lg:col-span-3">
            <p-card styleClass="h-[600px] flex flex-col">
              <!-- Messages Container -->
              <div 
                #messagesContainer
                class="flex-1 overflow-y-auto p-4 space-y-4"
              >
                <div *ngIf="messages.length === 0" class="text-center text-gray-500 py-8">
                  <p>Select a dataset and start asking questions!</p>
                </div>
                
                <div 
                  *ngFor="let message of messages"
                  class="flex"
                  [class.justify-end]="message.messageType === 'user'"
                  [class.justify-start]="message.messageType === 'assistant'"
                >
                  <div 
                    class="max-w-3xl rounded-lg p-4"
                    [class]="message.messageType === 'user' ? 'bg-primary-500 text-white' : 'bg-white border border-gray-200'"
                  >
                    <div class="flex items-start space-x-3">
                      <div class="flex-1">
                        <p class="text-sm">{{ message.content }}</p>
                        <div *ngIf="message.messageType === 'assistant'" class="mt-2 space-y-2">
                          <div *ngIf="message.confidenceScore !== undefined" class="flex items-center space-x-2">
                            <span class="text-xs text-gray-500">Confidence:</span>
                            <p-chip 
                              [label]="(message.confidenceScore * 100).toFixed(1) + '%'"
                              [class]="getConfidenceClass(message.confidenceScore)"
                            ></p-chip>
                          </div>
                          <div *ngIf="message.responseTimeMs !== undefined" class="flex items-center space-x-2">
                            <span class="text-xs text-gray-500">Response time:</span>
                            <span class="text-xs text-gray-600">{{ message.responseTimeMs }}ms</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="mt-2 text-xs opacity-70">
                      {{ message.timestamp | date:'short' }}
                    </div>
                  </div>
                </div>

                <!-- Loading indicator -->
                <div *ngIf="isLoading" class="flex justify-center">
                  <p-progressSpinner strokeWidth="4" animationDuration=".5s"></p-progressSpinner>
                </div>
              </div>

              <!-- Input Form -->
              <div class="border-t p-4">
                <form [formGroup]="chatForm" (ngSubmit)="sendMessage()" class="flex space-x-2">
                  <input
                    type="text"
                    pInputText
                    formControlName="message"
                    placeholder="Type your question..."
                    class="flex-1"
                    [disabled]="!selectedDataset || isLoading"
                  />
                  <p-button
                    type="submit"
                    icon="pi pi-send"
                    [disabled]="chatForm.invalid || !selectedDataset || isLoading"
                    [loading]="isLoading"
                  ></p-button>
                </form>
              </div>
            </p-card>
          </div>

          <!-- Sidebar -->
          <div class="lg:col-span-1">
            <div class="space-y-4">
              <!-- Conversation Starters -->
              <p-card header="Conversation Starters" styleClass="h-auto">
                <div class="space-y-2">
                  <p-button
                    *ngFor="let starter of conversationStarters"
                    [label]="starter.text"
                    severity="secondary"
                    size="small"
                    styleClass="w-full text-left justify-start"
                    (onClick)="useConversationStarter(starter.text)"
                    [disabled]="!selectedDataset || isLoading"
                  ></p-button>
                </div>
              </p-card>

              <!-- Dataset Info -->
              <p-card *ngIf="selectedDataset" header="Dataset Info" styleClass="h-auto">
                <div class="space-y-2">
                  <div>
                    <span class="text-sm font-medium text-gray-700">Name:</span>
                    <p class="text-sm text-gray-900">{{ selectedDataset.name }}</p>
                  </div>
                  <div>
                    <span class="text-sm font-medium text-gray-700">Description:</span>
                    <p class="text-sm text-gray-900">{{ selectedDataset.description }}</p>
                  </div>
                  <div>
                    <span class="text-sm font-medium text-gray-700">Documents:</span>
                    <p class="text-sm text-gray-900">{{ selectedDataset.documentCount }}</p>
                  </div>
                </div>
              </p-card>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class ChatComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;

  chatForm: FormGroup;
  datasets: Dataset[] = [];
  selectedDataset: Dataset | null = null;
  messages: ChatMessage[] = [];
  conversationStarters: ConversationStarter[] = [];
  isLoading = false;
  currentUser: any;

  constructor(
    private fb: FormBuilder,
    private chatService: ChatService,
    private datasetService: DatasetService,
    private authService: AuthService
  ) {
    this.chatForm = this.fb.group({
      message: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.loadDatasets();
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  loadDatasets(): void {
    this.datasetService.getDatasets().subscribe({
      next: (datasets) => {
        this.datasets = datasets.filter(d => d.isActive);
        if (this.datasets.length > 0) {
          this.selectedDataset = this.datasets[0];
          this.loadConversationStarters();
        }
      },
      error: (error) => {
        console.error('Error loading datasets:', error);
      }
    });
  }

  onDatasetChange(): void {
    if (this.selectedDataset) {
      this.messages = [];
      this.loadConversationStarters();
    }
  }

  loadConversationStarters(): void {
    if (!this.selectedDataset) return;

    this.chatService.getConversationStarters(this.selectedDataset.id).subscribe({
      next: (starters) => {
        this.conversationStarters = starters;
      },
      error: (error) => {
        console.error('Error loading conversation starters:', error);
      }
    });
  }

  sendMessage(): void {
    if (this.chatForm.valid && this.selectedDataset && !this.isLoading) {
      const messageText = this.chatForm.get('message')?.value;
      
      // Add user message to chat
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        conversationId: '',
        messageType: 'user',
        content: messageText,
        timestamp: new Date().toISOString()
      };
      this.messages.push(userMessage);

      // Clear form and set loading
      this.chatForm.reset();
      this.isLoading = true;

      // Prepare request
      const request: ChatRequest = {
        message: messageText,
        datasetId: this.selectedDataset.id
      };

      // Send message
      this.chatService.sendMessage(request).subscribe({
        next: (response: ChatResponse) => {
          // Add assistant response to chat
          const assistantMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            conversationId: response.conversationId,
            messageType: 'assistant',
            content: response.message,
            timestamp: new Date().toISOString(),
            confidenceScore: response.confidenceScore,
            responseTimeMs: response.responseTimeMs
          };
          this.messages.push(assistantMessage);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error sending message:', error);
          this.isLoading = false;
          
          // Add error message
          const errorMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            conversationId: '',
            messageType: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date().toISOString()
          };
          this.messages.push(errorMessage);
        }
      });
    }
  }

  useConversationStarter(text: string): void {
    this.chatForm.patchValue({ message: text });
    this.sendMessage();
  }

  getConfidenceClass(score: number): string {
    if (score >= 0.8) return 'bg-green-100 text-green-800';
    if (score >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  }

  scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) {}
  }

  logout(): void {
    this.authService.logout();
    // Redirect will be handled by auth guard
  }
} 